import React from 'react';
import { Box, ButtonBase, IconButton, Typography } from '@mui/material';
import { CloseIcon } from '@/components/icons';
import {
  launcherTeaserBubbleChromeSx,
  launcherTeaserBubbleTailSx,
} from '@/lib/launcher-teaser-styles';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

interface LauncherTeaserBubbleProps {
  message: string;
  onDismiss: () => void;
  onOpenChat: () => void;
  /** Preview mode: hide dismiss control. */
  showDismiss?: boolean;
}

export function LauncherTeaserBubble({
  message,
  onDismiss,
  onOpenChat,
  showDismiss = true,
}: LauncherTeaserBubbleProps) {
  const { t, locale } = useWidgetI18n();
  return (
    <Box
      sx={{
        ...launcherTeaserBubbleChromeSx,
        mb: 1.5,
        ...(showDismiss ? {} : { pr: 2 }),
      }}
    >
      {/* Visible copy sizes the bubble. It is hidden from AT so the name is not
          announced once as static text and again on the open control. */}
      <Typography
        component="span"
        variant="body2"
        aria-hidden
        sx={{ lineHeight: 1.45, fontWeight: 400, pr: showDismiss ? 0.5 : 0 }}
      >
        {message}
      </Typography>
      {/* Overlay button: sibling of dismiss, not a parent. Nesting the IconButton
          inside role="button" made JAWS/NVDA announce both names as one control. */}
      <ButtonBase
        type="button"
        disableRipple
        onClick={onOpenChat}
        aria-label={message}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          cursor: 'pointer',
          borderRadius: '12px',
          ...launcherTeaserBubbleTailSx,
          '&:focus-visible, &.Mui-focusVisible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: -2,
          },
        }}
      />
      {showDismiss && (
        <IconButton
          size="small"
          aria-label={t('launcher.dismissTeaser')}
          lang={locale}
          onClick={onDismiss}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 1,
            color: 'text.secondary',
            p: 0.25,
          }}
        >
          <CloseIcon aria-hidden sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}
